import React, { useState } from "react";
import { Col, Form, Row } from 'react-bootstrap';
import Contract, { ContractSplit, getContractTypeName } from "domain/contract";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { getProducerAccountDisplayName } from "features/settings/producers/producerAccount";
import useLocations from "features/locations/useLocations";
import ValidationError from "common/validationError";
import notificationService from "common/notificationService";
import SplitDetails from "./SplitDetails";
import contractService from "../contractService";
import loggingService from "common/logging/loggingService";
import ErrorList from "common/ui/ErrorList";
import ModalWrapper from "common/ui/Modal";
import "./SplitContractModal.scss";
import _ from "lodash";
import FormValidation, { useFormValidation } from "common/ui/FormValidation";
import useActiveMerchant from "features/merchants/useActiveMerchant";

type SplitContractProps = {
    contract: Contract;
    onClose(): void;
    onContractSplit?(): void;
};

const SplitContractModal: React.FC<SplitContractProps> = props => {

    const defaultSplit = getNewSplit(1);
    const [splits, setSplits] = useState<ContractSplit[]>([defaultSplit]);
    const [errors, setErrors] = useState<string[]>([]);

    const locations = useLocations();
    const detailsValidator = useFormValidation();
    const activeMerchant = useActiveMerchant();

    function getNewSplit(id: number): ContractSplit {
       return {
            id: id,
            customerAccount: props.contract.customerAccount,
            producer: props.contract.producer,
            deliveryLocation: props.contract.location
        } as ContractSplit;
    }

    async function handleSubmit() {
        const isValid = detailsValidator.current.validate();

        if (!isValid) return;

        try {
            await contractService.splitContract(props.contract.internalId, splits);
            notificationService.success(`Contract #${props.contract.displayId} has been successfully split.`);
            props.onContractSplit?.();
            props.onClose();
        }
        catch (err) {
            loggingService.error("Error splitting contract", err as Error);
            setErrors((err as ValidationError).errors ?? ["An unknown error occurred"]);
        }
    }

    function onAddSplitClicked() {
       const newSplit = getNewSplit((_.max(splits.map(s => s.id)) ?? 0) + 1);
       setSplits([...splits, newSplit]);
    }

    function isLoaded(): boolean {
        return locations.length > 0;
    }

    if (!isLoaded()) return <></>;

    return <ModalWrapper 
            className="split-contract-modal"
            title={`Split ${getContractTypeName(props.contract.contractType)} Contract #${props.contract.displayId}`}
            onAdvance={handleSubmit}
            onCancel={props.onClose}
            async> 

            <Form.Group as={Row}>
                <Form.Label column>ERP ID</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{props.contract.erpDisplayId}</div>
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column>Customer</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getProducerDisplayName(props.contract.producer)}</div>
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column>Account ID</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getProducerAccountDisplayName(props.contract.customerAccount)}</div>
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column>Delivery Location</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{props.contract.location?.name}</div>
                </Col>
            </Form.Group>

            {activeMerchant.generalSettings.displayInventoryLocation && <Form.Group as={Row}>
                <Form.Label column>Inventory Location</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{props.contract.inventoryLocation?.name}</div>
                </Col>
            </Form.Group>}

            <Form.Group as={Row} className="border-bottom pb-3">
                <Form.Label column>Quantity</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{props.contract.quantityContracted}</div>
                </Col>
            </Form.Group>

            <FormValidation ref={detailsValidator}>
                <SplitDetails
                    contract={props.contract}
                    activeMerchant={activeMerchant}
                    splits={splits} 
                    onSplitChanged={split => setSplits(splits?.map(s => s.id === split.id ? split : s))}
                    onRemove={split => setSplits(splits?.filter(s => s.id !== split.id))}
                />
            </FormValidation>
            <Form.Group as={Row} className="pb-3">
                <Col xs="4" className="pr-0">
                    <button type="button" className='link-button mr-auto float-right' onClick={onAddSplitClicked}>
                        +Add more splits
                    </button>
                </Col>
                <Col />
            </Form.Group>
        <ErrorList errors={errors} />
    </ModalWrapper>
}

export default SplitContractModal;
